import { createSlice, type Draft, type PayloadAction } from '@reduxjs/toolkit';
import {
  getPatientQueueSortOrderFromSession,
  type SortOrder,
} from './utils/patientQueueSortStorage';

type DashboardUIState = {
  isPatientQueueOpen: boolean;
  /**
   * When set, the Add to group call modal shows only patients in this room.
   * Cleared when the modal closes or when opening from a context without a room (e.g. header).
   */
  addToGroupCallRoomId: string | undefined;
  /**
   * Empty = all rooms (patient list + realtime). Non-empty = explicit subset only.
   * Persist `[]` when the user applies “all rooms”, not a copy of every room id.
   */
  patientQueueFilteredRoomIds: string[];
  patientQueueSortOrder: SortOrder;
  /** Whether the room filter callout should render while filters are active. */
  isRoomFilterAlertVisible: boolean;
  /**
   * Latches true when a patient checks in or a message arrives while the
   * provider is away from the dashboard. Resets to false when they navigate
   * back to the dashboard. Drives the blue activity dot on the nav.
   */
  hasAwayActivity: boolean;
};

const getInitialState = (): DashboardUIState => ({
  isPatientQueueOpen: false,
  addToGroupCallRoomId: undefined,
  patientQueueFilteredRoomIds: [],
  patientQueueSortOrder: getPatientQueueSortOrderFromSession() ?? 'asc',
  isRoomFilterAlertVisible: false,
  hasAwayActivity: false,
});

export const dashboardUISlice = createSlice({
  name: 'dashboardUI',
  initialState: () => getInitialState(),
  reducers: {
    setIsPatientQueueOpen: (
      state: Draft<DashboardUIState>,
      action: PayloadAction<boolean>,
    ): void => {
      state.isPatientQueueOpen = action.payload;
    },
    setPatientQueueFilteredRoomIds: (
      state: Draft<DashboardUIState>,
      action: PayloadAction<string[]>,
    ): void => {
      state.patientQueueFilteredRoomIds = action.payload;
    },
    setPatientQueueSortOrder: (
      state: Draft<DashboardUIState>,
      action: PayloadAction<SortOrder>,
    ): void => {
      state.patientQueueSortOrder = action.payload;
    },
    setAddToGroupCallRoomId: (
      state: Draft<DashboardUIState>,
      action: PayloadAction<string | undefined>,
    ): void => {
      state.addToGroupCallRoomId = action.payload;
    },
    setRoomFilterAlertVisibility: (
      state: Draft<DashboardUIState>,
      action: PayloadAction<boolean>,
    ): void => {
      state.isRoomFilterAlertVisible = action.payload;
    },
    setHasAwayActivity: (
      state: Draft<DashboardUIState>,
      action: PayloadAction<boolean>,
    ): void => {
      state.hasAwayActivity = action.payload;
    },
  },
});

export const {
  setIsPatientQueueOpen,
  setPatientQueueFilteredRoomIds,
  setPatientQueueSortOrder,
  setAddToGroupCallRoomId,
  setRoomFilterAlertVisibility,
  setHasAwayActivity,
} = dashboardUISlice.actions;
